import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./MSideDrawer.module.css";

export interface MSideDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

/**
 * MSideDrawer component that slides in from the left
 * Used for profile picker and similar left-side navigation
 * Renders via portal to ensure proper positioning at viewport level
 */
export const MSideDrawer: React.FC<MSideDrawerProps> = ({
  open,
  onClose,
  title,
  children,
}) => {
  const [isClosing, setIsClosing] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(open);

  // Handle opening and closing with animation
  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [open, shouldRender]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  if (!shouldRender) return null;

  // Safety check: ensure we're in browser environment and document.body exists
  if (typeof window === "undefined" || !document.body) {
    return null;
  }

  // Portal into .app-root so position:fixed children are contained within the
  // 430px app container (which has transform:translateZ(0) as containing block).
  // Falls back to document.body if .app-root is not found.
  const portalTarget = document.querySelector(".app-root") || document.body;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${isClosing ? styles.closing : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div className={`${styles.drawer} ${isClosing ? styles.closing : ""}`}>
        {/* Header */}
        {title && (
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
          </div>
        )}

        {/* Content */}
        <div className={styles.content}>{children}</div>
      </div>
    </>,
    portalTarget
  );
};

export default MSideDrawer;
